export const VALIDATION_ERROR_TYPES: QuoteErrorType[] = ['AGE_OUT_OF_RANGE', 'STATE_NOT_SUPPORTED'];

const KNOWN_LIMITATION_ERROR_TYPES: QuoteErrorType[] = [
  'AGE_OUT_OF_RANGE',
  'STATE_NOT_SUPPORTED',
  'NO_DEMOGRAPHICS_MATCH',
  'NO_CARRIER_MATCH',
];

export function isKnownLimitationError(error: QuoteError | null): error is QuoteError {
  return error !== null && KNOWN_LIMITATION_ERROR_TYPES.includes(error.type);
}

export function isUnexpectedError(error: QuoteError | null): error is QuoteError {
  return error !== null && error.type === 'UNEXPECTED_ERROR';
}

/**
 * @param quote - A quote that could be either PolicyQuote or TraditionalLTCQuote
 * @returns True if the quote is a TraditionalLTCQuote
 *
 * @example
 * ```typescript
 * if (isTraditionalLTCQuote(quote)) {
 *   // TypeScript knows quote is TraditionalLTCQuote here
 *   const premium = quote.primaryModalPremium;
 * }
 * ```
 */
export function isTraditionalLTCQuote(
  quote: PolicyQuote | TraditionalLTCQuote,
): quote is TraditionalLTCQuote {
  return 'traditionalLTCQuoteId' in quote && 'primaryModalPremium' in quote;
}

/**
 * @param quote - A quote that could be either PolicyQuote or TraditionalLTCQuote
 * @returns True if the quote is a PolicyQuote (life insurance)
 *
 * @example
 * ```typescript
 * if (isLifeInsuranceQuote(quote)) {
 *   // TypeScript knows quote is PolicyQuote here
 *   const premium = quote.premiumAmount;
 * }
 * ```
 */
export function isLifeInsuranceQuote(
  quote: PolicyQuote | TraditionalLTCQuote,
): quote is PolicyQuote {
  return 'insuranceQuoteId' in quote && 'premiumAmount' in quote;
}

/**
 * Short-term care rows come from ShortTermCareQuotes, not InsuranceQuotes, and
 * carry neither an insuranceQuoteId nor a traditionalLTCQuoteId. Anything
 * branching on quote shape MUST test this before falling through to a
 * life-insurance branch, or an STC quote is silently read as one.
 *
 * @param quote - A quote of any supported shape
 * @returns True if the quote is a ShortTermCareQuote
 */
export function isShortTermCareQuote(
  quote: PolicyQuote | TraditionalLTCQuote | ShortTermCareQuote,
): quote is ShortTermCareQuote {
  return 'shortTermCareQuoteId' in quote && 'modalPremium' in quote;
}

/**
 * @param quote - A quote that could be either PolicyQuote or TraditionalLTCQuote
 * @returns The appropriate quote ID for the quote type
 */
export function getQuoteId(
  quote: PolicyQuote | TraditionalLTCQuote | ShortTermCareQuote,
): string {
  if (isShortTermCareQuote(quote)) return quote.shortTermCareQuoteId;
  return isTraditionalLTCQuote(quote) ? quote.traditionalLTCQuoteId : quote.insuranceQuoteId;
}
